import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { parseConvoyagePath, normalizeConvoyageCanonicalPath, isKnownCitySlug } from './lib/seoSlugs';

const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard'));
const CreateMission = lazy(() => import('./pages/client/CreateMission'));
const DriverDashboard = lazy(() => import('./pages/driver/DriverDashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const MockPayment = lazy(() => import('./pages/payment/MockPayment'));
const CGU = lazy(() => import('./pages/CGU'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const Contact = lazy(() => import('./pages/Contact'));
const SeoLandingCity = lazy(() => import('./pages/seo/SeoLandingCity'));
const SeoLandingRoute = lazy(() => import('./pages/seo/SeoLandingRoute'));
const PublicMissionShare = lazy(() => import('./pages/public/PublicMissionShare'));
const PublicDriverProfile = lazy(() => import('./pages/public/PublicDriverProfile'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (process.env.NODE_ENV === 'development'
  ? 'http://127.0.0.1:8000'
  : 'https://convoyageos.onrender.com');

function AuthLoadingScreen() {
  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-md z-[9999] flex items-center justify-center">
      <div className="flex flex-col items-center">
        {/* Spinner premium avec dégradé et impulsion */}
        <div className="relative h-12 w-12">
          <div className="absolute inset-0 rounded-full border-[3px] border-slate-100 opacity-25"></div>
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-indigo-600 animate-spin shadow-sm"></div>
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-r-indigo-400/30 animate-[spin_1.5s_linear_infinite]"></div>
        </div>
        
        {/* Logo simplifié ou texte élégant */}
        <div className="mt-6 flex flex-col items-center gap-1">
          <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">ConvoyageOS</span>
          <div className="flex gap-1 mt-1">
            <div className="w-1 h-1 rounded-full bg-indigo-600 animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-1 h-1 rounded-full bg-indigo-600 animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-1 h-1 rounded-full bg-indigo-600 animate-bounce"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrivateRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (role && user.role !== role) return <Navigate to="/" />;
  return children;
}

const SEO_BY_LOCALE = {
  fr: {
    default: {
      title: 'ConvoyageOS | Convoyage voiture France Algerie',
      description: 'ConvoyageOS facilite le convoyage voiture France Algerie et le transport de vehicule en Europe avec suivi GPS et conducteurs verifies.',
    },
    routes: {
      '/': {
        title: 'ConvoyageOS | Convoyage voiture France Algerie',
        description: 'Plateforme de convoyage voiture France Algerie et de transport de vehicule avec conducteurs verifies et suivi GPS.',
      },
      '/login': {
        title: 'Connexion | ConvoyageOS',
        description: 'Connectez-vous a votre compte ConvoyageOS pour gerer vos missions et suivre vos transports en temps reel.',
      },
      '/register': {
        title: 'Inscription | ConvoyageOS',
        description: 'Inscrivez-vous sur ConvoyageOS en tant que client ou conducteur pour lancer vos missions de transport.',
      },
      '/verify-email': {
        title: 'Verification email | ConvoyageOS',
        description: 'Validez votre adresse email pour activer votre compte ConvoyageOS.',
      },
      '/forgot-password': {
        title: 'Mot de passe oublie | ConvoyageOS',
        description: 'Recuperez l acces a votre compte ConvoyageOS en reinitialisant votre mot de passe.',
      },
      '/how-it-works': {
        title: 'Comment ca marche : transport automobile et acheminement de vehicule | ConvoyageOS',
        description: 'Decouvrez le fonctionnement ConvoyageOS: publication de mission, offres conducteur, contrat numerique, paiement securise, suivi GPS et livraison en Europe et Afrique du Nord.',
      },
      '/contact': {
        title: 'Contact transport vehicule et convoyage | ConvoyageOS',
        description: 'Contactez ConvoyageOS pour vos demandes de transport de vehicule, convoyage et accompagnement client en Europe et Afrique du Nord.',
      },
      '/cgu': {
        title: 'CGU | ConvoyageOS',
        description: 'Consultez les conditions generales d utilisation de la plateforme ConvoyageOS.',
      },
      '/client/dashboard': {
        title: 'Tableau de bord client | ConvoyageOS',
        description: 'Pilotez vos missions de transport depuis votre espace client ConvoyageOS.',
      },
      '/client/create-mission': {
        title: 'Creer une mission | ConvoyageOS',
        description: 'Creez une mission de convoyage ou de transport en quelques etapes sur ConvoyageOS.',
      },
      '/driver/dashboard': {
        title: 'Tableau de bord conducteur | ConvoyageOS',
        description: 'Consultez vos missions et gerez votre activite de conducteur sur ConvoyageOS.',
      },
      '/admin/dashboard': {
        title: 'Administration | ConvoyageOS',
        description: 'Espace d administration ConvoyageOS pour la supervision des utilisateurs et missions.',
      },
      '/payment/mock': {
        title: 'Paiement | ConvoyageOS',
        description: 'Finalisez votre paiement de mission de facon securisee sur ConvoyageOS.',
      },
    },
  },
  en: {
    default: {
      title: 'ConvoyageOS | Vehicle delivery in Europe and North Africa',
      description: 'ConvoyageOS is a vehicle delivery and freight transport platform across France, Europe, and North Africa with live GPS tracking, vetted drivers, and secure payments.',
    },
    routes: {
      '/': {
        title: 'ConvoyageOS | Vehicle delivery in Europe and North Africa',
        description: 'ConvoyageOS is a vehicle delivery and freight transport platform across France, Europe, and North Africa with live GPS tracking, vetted drivers, and secure payments.',
      },
      '/login': {
        title: 'Login | ConvoyageOS',
        description: 'Sign in to your ConvoyageOS account to manage missions and track transport in real time.',
      },
      '/register': {
        title: 'Register | ConvoyageOS',
        description: 'Create your ConvoyageOS account as a customer or driver and start transport missions quickly.',
      },
      '/verify-email': {
        title: 'Email verification | ConvoyageOS',
        description: 'Verify your email address to activate your ConvoyageOS account.',
      },
      '/forgot-password': {
        title: 'Forgot password | ConvoyageOS',
        description: 'Recover access to your ConvoyageOS account by resetting your password.',
      },
      '/how-it-works': {
        title: 'How it works | ConvoyageOS',
        description: 'Learn how ConvoyageOS works: mission posting, offers, secure payments, and GPS tracking.',
      },
      '/contact': {
        title: 'Contact | ConvoyageOS',
        description: 'Contact the ConvoyageOS team for questions about vehicle delivery and freight transport.',
      },
      '/cgu': {
        title: 'Terms and conditions | ConvoyageOS',
        description: 'Read the terms and conditions for using the ConvoyageOS platform.',
      },
      '/client/dashboard': {
        title: 'Client dashboard | ConvoyageOS',
        description: 'Manage your transport missions from your ConvoyageOS client dashboard.',
      },
      '/client/create-mission': {
        title: 'Create mission | ConvoyageOS',
        description: 'Create a vehicle delivery or freight mission in a few steps with ConvoyageOS.',
      },
      '/driver/dashboard': {
        title: 'Driver dashboard | ConvoyageOS',
        description: 'Track missions and manage your driver activity on ConvoyageOS.',
      },
      '/admin/dashboard': {
        title: 'Admin dashboard | ConvoyageOS',
        description: 'ConvoyageOS administration area for user and mission monitoring.',
      },
      '/payment/mock': {
        title: 'Payment | ConvoyageOS',
        description: 'Complete your mission payment securely on ConvoyageOS.',
      },
    },
  },
};

function getSeoForPath(pathname, language) {
  const localeKey = language === 'en' ? 'en' : 'fr';
  const seoConfig = SEO_BY_LOCALE[localeKey];

  const slugToTitle = (slug) => (slug || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  if (pathname.startsWith('/payment/mock/')) {
    return seoConfig.routes['/payment/mock'];
  }

  const convoyagePath = parseConvoyagePath(pathname);
  if (convoyagePath.type === 'route') {
    const fromCity = slugToTitle(convoyagePath.fromSlug) || 'Paris';
    const toCity = slugToTitle(convoyagePath.toSlug) || 'Lyon';
    return {
      title: language === 'en'
        ? `Vehicle transport ${fromCity} to ${toCity} | ConvoyageOS`
        : `Transport de vehicule ${fromCity} vers ${toCity} | ConvoyageOS`,
      description: language === 'en'
        ? `Compare vehicle transport offers from ${fromCity} to ${toCity} and book a trusted driver in minutes.`
        : `Comparez les offres de transport et convoyage de vehicule de ${fromCity} vers ${toCity} et reservez rapidement.`,
    };
  }

  if (convoyagePath.type === 'city') {
    const city = slugToTitle(convoyagePath.citySlug) || 'France';
    return {
      title: language === 'en'
        ? `Vehicle Delivery in ${city} | ConvoyageOS`
        : `Transport et convoyage de vehicule a ${city} | ConvoyageOS`,
      description: language === 'en'
        ? `Book trusted vehicle delivery services in ${city} with live tracking and secure payment.`
        : `Reservez un service de transport et convoyage de vehicule a ${city} avec suivi en direct et paiement securise.`,
    };
  }

  if (pathname.startsWith('/convoyage-')) {
    const city = slugToTitle(pathname.replace('/convoyage-', '')) || 'France';
    return {
      title: language === 'en'
        ? `Vehicle Delivery in ${city} | ConvoyageOS`
        : `Transport et convoyage de vehicule a ${city} | ConvoyageOS`,
      description: language === 'en'
        ? `Book trusted vehicle delivery services in ${city} with live tracking and secure payment.`
        : `Reservez un service de transport et convoyage de vehicule a ${city} avec suivi en direct et paiement securise.`,
    };
  }

  if (pathname.startsWith('/livraison-vehicule-')) {
    const city = slugToTitle(pathname.replace('/livraison-vehicule-', '')) || 'France';
    return {
      title: language === 'en'
        ? `Car Transport in ${city} | ConvoyageOS`
        : `Livraison vehicule a ${city} | ConvoyageOS`,
      description: language === 'en'
        ? `Door-to-door car transport in ${city} with vetted drivers and mission tracking.`
        : `Transport de vehicule porte-a-porte a ${city} avec chauffeurs verifies.`,
    };
  }

  if (pathname.startsWith('/transport-')) {
    const routeSlug = pathname.replace('/transport-', '');
    const [fromSlug, toSlug] = routeSlug.split('-').filter(Boolean);
    const fromCity = slugToTitle(fromSlug) || 'Paris';
    const toCity = slugToTitle(toSlug) || 'Lyon';
    return {
      title: language === 'en'
        ? `Vehicle transport ${fromCity} to ${toCity} | ConvoyageOS`
        : `Transport de vehicule ${fromCity} vers ${toCity} | ConvoyageOS`,
      description: language === 'en'
        ? `Compare vehicle transport offers from ${fromCity} to ${toCity} and book a trusted driver in minutes.`
        : `Comparez les offres de transport et convoyage de vehicule de ${fromCity} vers ${toCity} et reservez rapidement.`,
    };
  }

  return seoConfig.routes[pathname] || seoConfig.default;
}

function setOrCreateMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setOrCreateLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setOrCreateJsonLdScript(scriptId, data) {
  let element = document.head.querySelector(`#${scriptId}`);
  if (!element) {
    element = document.createElement('script');
    element.setAttribute('type', 'application/ld+json');
    element.setAttribute('id', scriptId);
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function RouteSeoManager() {
  const location = useLocation();
  const { language } = useLanguage();
  const [remoteSeo, setRemoteSeo] = useState(null);

  useEffect(() => {
    let isCancelled = false;
    const pathname = location.pathname || '/';
    const lang = language === 'en' ? 'en' : 'fr';

    async function loadRemoteSeo() {
      const isPrivatePath = /^\/(admin|client|driver)(\/|$)/.test(pathname);
      if (isPrivatePath) {
        if (!isCancelled) setRemoteSeo(null);
        return;
      }

      try {
        let visitorId = '';
        try {
          visitorId = localStorage.getItem('seo_visitor_id') || '';
          if (!visitorId) {
            visitorId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem('seo_visitor_id', visitorId);
          }
        } catch (_storageError) {
          visitorId = '';
        }

        const query = new URLSearchParams({
          path: pathname,
          lang,
        });
        if (visitorId) {
          query.set('visitor_id', visitorId);
        }

        const response = await fetch(`${BACKEND_URL}/api/seo/meta?${query.toString()}`);
        if (!response.ok) {
          if (!isCancelled) setRemoteSeo(null);
          return;
        }
        const payload = await response.json();
        if (!isCancelled) {
          if (payload?.found && payload?.title && payload?.description) {
            setRemoteSeo({ title: payload.title, description: payload.description });
          } else {
            setRemoteSeo(null);
          }
        }
      } catch (_error) {
        if (!isCancelled) setRemoteSeo(null);
      }
    }

    loadRemoteSeo();
    return () => {
      isCancelled = true;
    };
  }, [location.pathname, language]);

  useEffect(() => {
    const baseUrl = 'https://convoyageos.com';
    const pathname = location.pathname || '/';
    const canonicalPath = normalizeConvoyageCanonicalPath(pathname);
    const seo = remoteSeo || getSeoForPath(pathname, language);
    const canonicalUrl = `${baseUrl}${canonicalPath}`;
    const alternateFrUrl = `${baseUrl}${canonicalPath}?lang=fr`;
    const alternateEnUrl = `${baseUrl}${canonicalPath}?lang=en`;
    const ogLocale = language === 'en' ? 'en_US' : 'fr_FR';
    const alternateLocale = language === 'en' ? 'fr_FR' : 'en_US';
    const ogImage = `${baseUrl}/logo512.png`;
    const isNoIndexPath = /^\/(admin|client|driver)(\/|$)|^\/(login|register|verify-email|forgot-password)(\/|$)|^\/payment\/mock(\/|$)/.test(pathname);

    document.title = seo.title;

    setOrCreateMeta('meta[name="description"]', {
      name: 'description',
      content: seo.description,
    });

    setOrCreateMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: seo.title,
    });
    setOrCreateMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: seo.description,
    });
    setOrCreateMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    });
    setOrCreateMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: 'ConvoyageOS',
    });
    setOrCreateMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalUrl,
    });
    setOrCreateMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: ogLocale,
    });
    setOrCreateMeta('meta[property="og:locale:alternate"]', {
      property: 'og:locale:alternate',
      content: alternateLocale,
    });
    setOrCreateMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: ogImage,
    });

    setOrCreateMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: seo.title,
    });
    setOrCreateMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: seo.description,
    });
    setOrCreateMeta('meta[name="twitter:url"]', {
      name: 'twitter:url',
      content: canonicalUrl,
    });
    setOrCreateMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: ogImage,
    });
    setOrCreateMeta('meta[name="robots"]', {
      name: 'robots',
      content: isNoIndexPath ? 'noindex, nofollow' : 'index, follow',
    });

    setOrCreateLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: canonicalUrl,
    });

    setOrCreateLink('link[rel="alternate"][hreflang="fr"]', {
      rel: 'alternate',
      hreflang: 'fr',
      href: alternateFrUrl,
    });
    setOrCreateLink('link[rel="alternate"][hreflang="en"]', {
      rel: 'alternate',
      hreflang: 'en',
      href: alternateEnUrl,
    });
    setOrCreateLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: 'alternate',
      hreflang: 'x-default',
      href: canonicalUrl,
    });

    if (!isNoIndexPath) {
      setOrCreateJsonLdScript('convoyageos-organization-ld', {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ConvoyageOS',
        url: baseUrl,
        logo: `${baseUrl}/logo512.png`,
        sameAs: [
          'https://www.linkedin.com',
          'https://x.com',
        ],
      });

      setOrCreateJsonLdScript('convoyageos-website-ld', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'ConvoyageOS',
        url: baseUrl,
        inLanguage: language === 'en' ? 'en' : 'fr',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${baseUrl}/convoyage/{search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      });
    }
  }, [location.pathname, language, remoteSeo]);

  return null;
}

function SeoRouteResolver() {
  const { ville1, ville2 } = useParams();
  if (isKnownCitySlug(ville1) && isKnownCitySlug(ville2) && ville1 !== ville2) {
    return <SeoLandingRoute />;
  }
  return <SeoLandingCity mode="convoyage" slugOverride={`${ville1}-${ville2}`} />;
}

function AttributionTracker() {
  const location = useLocation();

  useEffect(() => {
    const search = new URLSearchParams(location.search || '');
    const utmSource = (search.get('utm_source') || '').trim();
    const utmMedium = (search.get('utm_medium') || '').trim();
    const utmCampaign = (search.get('utm_campaign') || '').trim();
    const refTypeFromQuery = (search.get('ref_type') || '').trim();
    const refIdFromQuery = (search.get('ref_id') || '').trim();

    let refType = refTypeFromQuery;
    let refId = refIdFromQuery;
    if (!refType || !refId) {
      const missionMatch = location.pathname.match(/^\/mission\/([^/]+)/);
      const driverMatch = location.pathname.match(/^\/conducteur\/([^/]+)/);
      if (missionMatch) {
        refType = refType || 'mission';
        refId = refId || missionMatch[1];
      }
      if (driverMatch) {
        refType = refType || 'driver';
        refId = refId || driverMatch[1];
      }
    }

    const hasAttribution = Boolean(utmSource || utmMedium || utmCampaign || refType || refId);
    if (!hasAttribution) return;

    const payload = {
      utm_source: utmSource || null,
      utm_medium: utmMedium || null,
      utm_campaign: utmCampaign || null,
      ref_type: refType || null,
      ref_id: refId || null,
      landing_path: `${location.pathname || '/'}${location.search || ''}`,
      captured_at: new Date().toISOString(),
    };

    localStorage.setItem('marketing_attribution', JSON.stringify(payload));
  }, [location.pathname, location.search]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <RouteSeoManager />
          <AttributionTracker />
          <SessionTimeoutModal />
          <Navbar />
          <Suspense fallback={<AuthLoadingScreen />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/cgu" element={<CGU />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/convoyage/:ville" element={<SeoLandingCity mode="convoyage" />} />
              <Route path="/convoyage/:ville1-:ville2" element={<SeoRouteResolver />} />
              <Route path="/convoyage-:ville" element={<SeoLandingCity mode="convoyage" />} />
              <Route path="/livraison-vehicule-:ville" element={<SeoLandingCity mode="livraison" />} />
              <Route path="/transport-:ville1-:ville2" element={<SeoLandingRoute />} />
              <Route path="/mission/:missionId" element={<PublicMissionShare />} />
              <Route path="/conducteur/:driverId" element={<PublicDriverProfile />} />
              
              <Route path="/client/dashboard" element={
                <PrivateRoute role="client"><ClientDashboard /></PrivateRoute>
              } />
              <Route path="/client/create-mission" element={
                <PrivateRoute role="client"><CreateMission /></PrivateRoute>
              } />
              <Route path="/client" element={<Navigate to="/client/dashboard" replace />} />
              <Route path="/client/*" element={<Navigate to="/client/dashboard" replace />} />
              <Route path="/payment/mock/:missionId" element={<MockPayment />} />
              
              <Route path="/driver/dashboard" element={
                <PrivateRoute role="driver"><DriverDashboard /></PrivateRoute>
              } />
              
              <Route path="/admin/dashboard" element={
                <PrivateRoute role="admin"><AdminDashboard /></PrivateRoute>
              } />
            </Routes>
          </Suspense>
          <Toaster />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
